import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, CalendarPlus, TrendingUp, Users, Car, BadgeDollarSign, Hourglass, Award, Percent, SlidersHorizontal, ShieldAlert, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const PRESETS = [["today", "Today"], ["week", "Week"], ["month", "Month"], ["year", "Year"], ["all", "All Time"], ["custom", "Custom"]];

const Stat = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4">
    <div className="flex items-center gap-2 text-white/45 text-[11px] uppercase tracking-wider"><Icon size={13} className="text-[#F2B705]" /> {label}</div>
    <div className={`font-display font-black text-2xl mt-1 ${accent || ""}`}>{value}</div>
    {sub && <div className="text-white/40 text-[11px] mt-0.5">{sub}</div>}
  </div>
);

const TopTable = ({ title, icon: Icon, rows, testid }) => (
  <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4" data-testid={testid}>
    <div className="font-display font-bold text-sm mb-2 flex items-center gap-2"><Icon size={15} className="text-[#F2B705]" /> {title}</div>
    {rows.length === 0 ? <p className="text-white/40 text-xs">No extensions in this period.</p> : (
      <table className="w-full text-xs">
        <thead><tr className="text-white/40 text-left"><th className="pb-1.5 font-medium">Name</th><th className="pb-1.5 font-medium">Ext</th><th className="pb-1.5 font-medium">Days</th><th className="pb-1.5 font-medium text-right">Revenue</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.label} className="border-t border-white/5"><td className="py-1.5 pr-2">{r.label}</td><td>{r.count}</td><td>{r.days}</td><td className="text-right text-emerald-400 font-semibold">{money(r.revenue)}</td></tr>
        ))}</tbody>
      </table>
    )}
  </div>
);

const AlertSettings = ({ token }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/extensions/alert-settings`, auth).then((r) => setS(r.data)).catch(() => {}); // eslint-disable-next-line
  }, [token]);
  if (!s) return null;
  const fields = [["large_discount_dollar", "Large discount ≥ $"], ["large_discount_pct", "…or ≥ % of subtotal"],
    ["max_extension_days", "Max extension days (warn)"], ["unpaid_escalation_hours", "Unpaid escalation (hours)"],
    ["approach_return_hours", "Approaching-return alert (hours before)"], ["many_extensions_threshold", "Alert after N extensions"]];
  const save = async () => {
    setSaving(true);
    try { const { data } = await axios.put(`${API}/extensions/alert-settings`, s, auth); setS(data); toast.success("Alert thresholds saved."); }
    catch { toast.error("Could not save thresholds."); } finally { setSaving(false); }
  };
  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4" data-testid="ext-alert-settings">
      <div className="font-display font-bold text-sm mb-2 flex items-center gap-2"><SlidersHorizontal size={15} className="text-[#F2B705]" /> Smart Alert Thresholds</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {fields.map(([k, lbl]) => (
          <label key={k} className="text-[11px] text-white/50">{lbl}
            <input data-testid={`ext-setting-${k}`} type="number" step="1" min="0" value={s[k]}
              onChange={(e) => setS({ ...s, [k]: e.target.value })}
              className="mt-1 w-full bg-black border border-white/15 rounded px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-[#F2B705]/60" />
          </label>
        ))}
      </div>
      <button data-testid="ext-settings-save" onClick={save} disabled={saving}
        className="mt-3 text-xs font-bold px-4 py-2 rounded-md bg-[#F2B705] text-black hover:bg-white disabled:opacity-60">
        {saving ? "Saving…" : "Save Thresholds"}
      </button>
    </div>
  );
};

const CoverageScan = ({ token }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [open, setOpen] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    axios.get(`${API}/extensions/coverage-scan?include_resolved=${showResolved}`, auth)
      .then((r) => setData(r.data)).catch(() => toast.error("Coverage scan failed."));
    // eslint-disable-next-line
  }, [token, showResolved]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const resolve = async (r, resolved) => {
    setBusy(r.ext_id);
    try {
      await axios.post(`${API}/extensions/coverage-scan/${r.ext_id}/resolve`, { resolved }, auth);
      toast.success(resolved ? "Marked resolved." : "Reopened.");
      load();
    } catch { toast.error("Update failed."); } finally { setBusy(""); }
  };
  const openRental = (r) => window.dispatchEvent(new CustomEvent("autohire:open-booking", { detail: r.booking_id }));
  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4" data-testid="coverage-scan">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button data-testid="coverage-scan-toggle" onClick={() => setOpen((o) => !o)}
          className="font-display font-bold text-sm flex items-center gap-2 hover:text-[#F2B705]">
          <ShieldAlert size={15} className="text-[#F2B705]" /> Coverage Exceptions
          {data && data.count > 0 && <span className="bg-red-500/20 text-red-300 border border-red-500/30 text-[10px] font-bold rounded-full px-2 py-0.5">{data.count} open · {money(data.total_missed)} uncollected</span>}
          {open ? <ChevronUp size={14} className="text-white/40" /> : <ChevronDown size={14} className="text-white/40" />}
        </button>
        {open && (
          <label className="flex items-center gap-1.5 text-[11px] text-white/50 cursor-pointer">
            <input data-testid="coverage-scan-show-resolved" type="checkbox" checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)} className="h-3.5 w-3.5 accent-[#F2B705]" />
            Show resolved
          </label>
        )}
      </div>
      <p className="text-white/40 text-[11px] mt-1">Past extensions where the rental had All-In Coverage but the extension was charged $0 for insurance — review and collect.</p>
      {open && (!data ? <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#F2B705]" size={18} /></div> : (
        data.items.length === 0 ? <p className="text-emerald-400 text-xs mt-3">✓ No coverage exceptions found — every extension charged insurance correctly.</p> : (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-xs min-w-[900px]">
              <thead><tr className="text-white/40 text-left">
                <th className="pb-1.5 pr-2 font-medium">Customer</th><th className="pb-1.5 pr-2 font-medium">Vehicle</th>
                <th className="pb-1.5 pr-2 font-medium">Booking #</th><th className="pb-1.5 pr-2 font-medium">Insurance</th>
                <th className="pb-1.5 pr-2 font-medium">Extension Dates</th><th className="pb-1.5 pr-2 font-medium">Missed Days</th>
                <th className="pb-1.5 pr-2 font-medium text-right">Uncollected</th><th className="pb-1.5 pr-2 font-medium">Status</th>
                <th className="pb-1.5 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody>{data.items.map((r) => (
                <tr key={r.ext_id} data-testid={`coverage-row-${r.ext_id}`} className={`border-t border-white/5 ${r.resolved ? "opacity-50" : ""}`}>
                  <td className="py-2 pr-2 font-semibold">{r.customer}</td>
                  <td className="pr-2 text-white/70">{r.vehicle}</td>
                  <td className="pr-2 text-white/50 font-mono">{(r.booking_id || "").slice(0, 8)}</td>
                  <td className="pr-2 text-white/60">{r.insurance_label}</td>
                  <td className="pr-2 text-white/60">{r.prev_return_date} → {r.new_return_date} <span className="text-white/35">(Ext #{r.number})</span></td>
                  <td className="pr-2">{r.days} × {money(r.coverage_rate)}</td>
                  <td className="pr-2 text-right text-red-400 font-bold">{money(r.missed_amount)}</td>
                  <td className="pr-2">
                    <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 border ${r.resolved ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" : "text-amber-300 border-amber-400/30 bg-amber-400/10"}`}>
                      {r.resolved ? "Resolved" : "Open"}
                    </span>
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button data-testid={`coverage-open-rental-${r.ext_id}`} onClick={() => openRental(r)}
                      className="text-[10px] font-bold text-[#F2B705] border border-[#F2B705]/40 rounded px-2 py-1 hover:bg-[#F2B705] hover:text-black mr-1.5 inline-flex items-center gap-1">
                      <ExternalLink size={10} /> Open Rental
                    </button>
                    <button data-testid={`coverage-resolve-${r.ext_id}`} onClick={() => resolve(r, !r.resolved)} disabled={busy === r.ext_id}
                      className="text-[10px] font-bold text-white/60 border border-white/20 rounded px-2 py-1 hover:bg-white hover:text-black disabled:opacity-50">
                      {busy === r.ext_id ? "…" : r.resolved ? "Reopen" : "Resolve"}
                    </button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )
      ))}
    </div>
  );
};

export default function ExtensionAnalyticsModule({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [preset, setPreset] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(() => {
    setData(null);
    axios.get(`${API}/extensions/analytics?preset=${preset}&start=${start}&end=${end}`, auth)
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load extension analytics."));
    // eslint-disable-next-line
  }, [preset, start, end, token]);
  useEffect(() => { if (preset !== "custom" || (start && end)) load(); }, [load, preset, start, end]);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/extensions/analytics/export?preset=${preset}&start=${start}&end=${end}`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `extension_analytics_${preset}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); } finally { setDownloading(false); }
  };

  const totalDW = data ? (data.daily_revenue + data.weekly_revenue) || 1 : 1;
  return (
    <div className="space-y-4" data-testid="extension-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {PRESETS.map(([k, lbl]) => (
            <button key={k} data-testid={`ext-preset-${k}`} onClick={() => setPreset(k)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${preset === k ? "bg-[#F2B705] text-black border-[#F2B705]" : "border-white/15 text-white/60 hover:bg-white/5"}`}>{lbl}</button>
          ))}
          {preset === "custom" && (
            <span className="flex items-center gap-1.5 ml-1">
              <input data-testid="ext-custom-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-black border border-white/15 rounded px-2 py-1 text-xs text-white [color-scheme:dark]" />
              <span className="text-white/40 text-xs">→</span>
              <input data-testid="ext-custom-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-black border border-white/15 rounded px-2 py-1 text-xs text-white [color-scheme:dark]" />
            </span>
          )}
        </div>
        <button data-testid="ext-analytics-download" onClick={download} disabled={downloading}
          className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-md border border-[#F2B705]/50 text-[#F2B705] hover:bg-[#F2B705]/10 disabled:opacity-60">
          {downloading ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />} Export CSV
        </button>
      </div>

      {!data ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#F2B705]" /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            <Stat icon={BadgeDollarSign} label="Extension revenue" value={money(data.total_revenue)} sub={`${money(data.total_collected)} collected`} accent="text-emerald-400" testid="ext-stat-revenue" />
            <Stat icon={CalendarPlus} label="Extensions" value={data.total_extensions} testid="ext-stat-count" />
            <Stat icon={TrendingUp} label="Days sold" value={data.total_days_sold} sub={`avg ${data.avg_length_days} days`} testid="ext-stat-days" />
            <Stat icon={BadgeDollarSign} label="Avg / extension" value={money(data.avg_revenue_per_extension)} testid="ext-stat-avg" />
            <Stat icon={Hourglass} label="Outstanding" value={money(data.outstanding_balances)} accent="text-amber-300" testid="ext-stat-outstanding" />
            <Stat icon={Award} label="Points issued" value={(data.loyalty_points_issued || 0).toLocaleString()} sub={`${money(data.discounts_given)} in discounts`} testid="ext-stat-points" />
          </div>

          <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4" data-testid="ext-daily-vs-weekly">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-white/45 mb-2">
              <span className="flex items-center gap-1.5"><Percent size={12} className="text-[#F2B705]" /> Daily vs Weekly pricing revenue</span>
              <span className="text-white/70">{money(data.daily_revenue)} daily · {money(data.weekly_revenue)} weekly</span>
            </div>
            <div className="h-2.5 bg-white/10 rounded-full overflow-hidden flex">
              <div className="h-full bg-[#F2B705]" style={{ width: `${(data.daily_revenue / totalDW) * 100}%` }} />
              <div className="h-full bg-emerald-500" style={{ width: `${(data.weekly_revenue / totalDW) * 100}%` }} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2.5">
            <TopTable title="Most extended vehicles" icon={Car} rows={data.by_vehicle} testid="ext-by-vehicle" />
            <TopTable title="Most extended customers" icon={Users} rows={data.by_customer} testid="ext-by-customer" />
            <TopTable title="Revenue by employee" icon={Users} rows={data.by_employee} testid="ext-by-employee" />
          </div>

          <CoverageScan token={token} />
          <AlertSettings token={token} />
        </>
      )}
    </div>
  );
}
